import { useMemo } from 'react'
import { useQuery } from 'urql'
import { PIPELINES_CATALOG_QUERY } from './graphql'
import { mergeSystemPipelines } from './systemPipelines'
import type { ActionCenterPipelinesQuery } from 'app/generated/gql/graphql'
import { isCompanionPipeline } from 'lib/companionPipelines'

export type PipelineStageInfo = { name: string; color?: string; order?: number }
export type PipelineCatalogEntry = {
  id: string
  name: string
  stages: PipelineStageInfo[]
  /** A stage only a booking sets: drawn, never offered as a choice, and a contact in it cannot be moved by hand. */
  lockedStage?: string
}

/**
 * The workspace's pipelines with their stages, plus the system pipelines,
 * which are constants rather than records and have to be merged in here or a
 * membership on a reserved id renders as a bare label with no ladder.
 *
 * Deliberately not gated on `ACTION_CENTER_FLAG`, unlike the other three places
 * that merge them (`PipelineStore.fetchList`, `usePipelines`,
 * `pipelineFilterFetchers`). This catalog is the Action Center's own, read only
 * by `FocusModeComponent` behind `<FeatureRoute name="action-center">`, so the
 * flag is already true by the time anything calls it: a gate here could only
 * ever fire wrongly, and firing wrongly costs `PipelineRail` the very ladder
 * this merge exists to give it. Its picker is filtered separately, by
 * `isAssignablePipeline` in `joinablePipelines`.
 */
export function usePipelinesCatalog(): PipelineCatalogEntry[] | undefined {
  const [result] = useQuery<ActionCenterPipelinesQuery>({ query: PIPELINES_CATALOG_QUERY })
  const pipelines = result.data?.pipelines
  return useMemo(() => {
    if (!pipelines) return undefined
    return mergeSystemPipelines(
      pipelines
        .filter((pipeline) => !isCompanionPipeline(pipeline))
        .map((pipeline) => ({
          id: pipeline.id,
          name: pipeline.name,
          stages: [...(pipeline.stages ?? [])]
            .map((stage) => ({ name: stage.name, color: stage.color ?? undefined, order: stage.order ?? undefined }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        })),
    )
  }, [pipelines])
}
